import { Reveal } from "./Reveal";
import { Thread } from "./Thread";
import { PullQuote } from "./PullQuote";
import { GoogleStars } from "./GoogleStars";
import { SocialLinks } from "./SocialLinks";
import { trackEvent } from "../lib/tracking";
import {
  GOOGLE_PROFILE_URL,
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
} from "../lib/googleReviews";

const PROOF_POINTS = [
  "CPSM · NCS",
  "Newborn through preschool experience",
  "Professional childcare background",
  "Behavioral intervention experience",
  "Denver, Colorado",
  "You work directly with Andrea",
];

const FIRST_QUESTIONS = [
  "How did your child fall asleep last night?",
  "What happens when you put her down?",
  "When did they wake?",
  "What did you do when they did?",
  "And what did you do next?",
  "What did naps look like yesterday?",
  "When did you first notice things changing?",
  "What have you already tried?",
];

export const MeetAndrea = () => (
  <section id="meet-andrea" data-testid="meet-andrea-section" className="scroll-mt-24 relative bg-blushfield overflow-hidden">
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pt-16 lg:pt-24">
      <Thread className="text-clay/60" />
    </div>

    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pb-24 lg:pb-36 pt-10 lg:pt-16 flex flex-col lg:grid lg:grid-cols-12 lg:gap-x-8">

      {/* 1 — the turn toward the human */}
      <Reveal className="order-1 lg:col-span-6 lg:col-start-7 lg:row-start-1 lg:self-end">
        <p className="text-xs tracking-[0.25em] uppercase font-semibold text-clay">
          The person on the other side of this
        </p>
      </Reveal>

      {/* 2 — the portrait, treated editorially */}
      <Reveal delay={0.08} className="order-2 mt-10 lg:mt-0 lg:col-span-5 lg:row-span-2 lg:row-start-1 relative self-start">
        <div aria-hidden="true" className="absolute inset-0 translate-x-4 translate-y-4 rounded-[2rem] bg-white/50" />
        <img
          src="/andrea-portrait.webp"
          alt="Andrea Hurley, certified pediatric sleep consultant, seated at a wooden table with a coffee and an open notebook"
          loading="lazy"
          data-testid="andrea-portrait"
          className="relative w-full aspect-[3/4] object-cover object-[38%_25%] rounded-[2rem] shadow-[0_24px_50px_-25px_rgba(42,33,24,0.3)]"
        />
        <p
          data-testid="andrea-credential-note"
          className="absolute -bottom-5 right-4 sm:right-8 bg-ivory px-5 py-3.5 rounded-xl shadow-[0_16px_40px_-15px_rgba(42,33,24,0.35)] text-[11px] tracking-[0.3em] uppercase font-semibold text-ink"
        >
          CPSM · NCS
        </p>
      </Reveal>

      {/* 3 — name, role, rating, then Andrea's own voice */}
      <div className="order-3 mt-14 lg:mt-8 lg:col-span-6 lg:col-start-7 lg:row-start-2">
        <Reveal>
          <h2 className="font-serif font-light text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            Andrea Hurley
          </h2>
          <p className="mt-4 font-serif italic font-light text-2xl sm:text-3xl text-euca leading-snug">
            Certified Pediatric Sleep Consultant
          </p>
          <div className="mt-5">
            <p
              data-testid="andrea-google-rating"
              className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs tracking-[0.18em] uppercase font-semibold text-ink/70"
            >
              <GoogleStars size={12} />
              Google Reviews · {GOOGLE_RATING} · {GOOGLE_REVIEW_COUNT} reviews
            </p>
            <a
              data-testid="andrea-google-reviews-link"
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                trackEvent("google_reviews_click", { location: "meet_andrea" })
              }
              className="mt-2.5 inline-flex items-center gap-1.5 py-2.5 -my-2 text-xs tracking-[0.18em] uppercase font-semibold text-clay transition-colors hover:text-ink"
            >
              Read Reviews on Google
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-12 font-serif font-light text-ink text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.14] tracking-tight">
            Before I tell you
            <span className="block">what I think,</span>
            <span className="block italic text-clay">I want to know</span>
            <span className="block italic text-clay">what happened.</span>
          </p>
        </Reveal>

        <Reveal delay={0.14}>
          <div className="mt-9 space-y-3 font-serif italic font-light text-xl sm:text-2xl leading-snug text-ink/70 max-w-xl">
            {FIRST_QUESTIONS.map((q) => (
              <p key={q}>{q}</p>
            ))}
          </div>
          <p className="mt-9 text-sm font-normal text-smoke">
            And here&rsquo;s one I really want to know:
          </p>
          <p className="mt-4 font-serif font-light text-ink text-2xl sm:text-3xl leading-snug">
            What worked —
            <span className="block italic text-clay">even if it only</span>
            <span className="block italic text-clay">worked for a little while?</span>
          </p>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-9 max-w-lg space-y-5 text-base sm:text-lg leading-relaxed font-normal text-smoke">
            <p>
              Those details matter to me. Because &ldquo;my baby wakes at
              night&rdquo; can describe a hundred completely different
              situations. And I don&rsquo;t want to give you an answer until I
              understand yours.
            </p>
          </div>
          <p className="mt-10 font-serif font-light text-ink text-2xl sm:text-3xl lg:text-4xl leading-[1.18] tracking-tight max-w-xl">
            I don&rsquo;t need your baby
            <span className="block">to sleep like</span>
            <span className="block">somebody else&rsquo;s baby.</span>
            <span className="block mt-5 italic text-clay">I need to understand how</span>
            <span className="block italic text-clay">your baby is sleeping — and what</span>
            <span className="block italic text-clay">you&rsquo;re trying to change.</span>
          </p>
        </Reveal>

        <Reveal delay={0.22}>
          <p className="mt-12 font-serif italic font-light text-2xl sm:text-3xl text-clay leading-snug">
            A real person behind the plan.
          </p>
          <div className="mt-8 max-w-lg space-y-5 text-base sm:text-lg leading-relaxed font-normal text-smoke">
            <p>
              I&rsquo;ve spent twenty years caring for children — as a private
              nanny, an in-home daycare provider, and a school behavioral
              interventionist. Newborns through preschool.
            </p>
            <p>
              Real rooms, real routines, real families: a perspective that goes
              beyond sleep theory.
            </p>
            <p>
              I trained at the Center for Pediatric Sleep Management, and I work
              as an infant and toddler sleep consultant — but the job, the way
              I do it, starts before any advice: understand what&rsquo;s
              actually happening first.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.24}>
          <div id="virtual-sleep-support" data-testid="virtual-sleep-support" className="mt-10 max-w-lg scroll-mt-24">
            <h3 className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Virtual pediatric sleep support
            </h3>
            <div className="mt-5 space-y-5 text-base sm:text-lg leading-relaxed font-normal text-smoke">
              <p>
                And you don&rsquo;t have to live down the street for us to work
                through this together. Sleep happens at home — that&rsquo;s where
                we&rsquo;ll work through it, on a call, one on one.{" "}
                <a href="/virtual-sleep-consultant" className="text-ink underline decoration-clay/50 underline-offset-4 hover:text-claydark transition-colors duration-300">
                  See how virtual sleep support works
                </a>
                .
              </p>
              <p>
                If you&rsquo;re in the{" "}
                <a href="/denver-sleep-consultant" className="text-ink underline decoration-clay/50 underline-offset-4 hover:text-claydark transition-colors duration-300">
                  Denver area
                </a>
                ,{" "}
                <a href="/denver-in-home-sleep-consultant" className="text-ink underline decoration-clay/50 underline-offset-4 hover:text-claydark transition-colors duration-300">
                  in-person support
                </a>{" "}
                may also be available. We can talk about
                that when we talk.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.26}>
          <div className="mt-10 max-w-lg" data-testid="andrea-proof-rail">
            {PROOF_POINTS.map((p) => (
              <p
                key={p}
                className="flex items-baseline gap-4 py-3.5 border-t border-ink/10 last:border-b text-xs sm:text-sm tracking-[0.2em] uppercase font-semibold text-ink/75"
              >
                <span aria-hidden="true" className="h-px w-5 shrink-0 bg-clay translate-y-[-4px]" />
                {p}
              </p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.28}>
          <div className="mt-12 max-w-lg" data-testid="andrea-social">
            <p className="text-xs tracking-[0.25em] uppercase font-semibold text-euca">
              Follow Andrea
            </p>
            <SocialLinks location="andrea_bio" variant="light" className="mt-3 -ml-2.5" />
          </div>
        </Reveal>
      </div>
    </div>

    {/* Verified Google proof — Caitlyn */}
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-20 pb-24 lg:pb-32">
      <div className="pt-14 lg:pt-16 border-t border-ink/10">
        <PullQuote
          testid="review-caitlyn"
          quote="Working with Andrea has been transformative. Her expertise, patience, and personalized approach made navigating sleep challenges straightforward and manageable. She provides practical strategies with compassion…"
          name="Caitlyn Shafner-Wert"
        />
      </div>
    </div>
  </section>
);
